/**
 * Comparing two identity numbers that are written differently (MAR-179).
 *
 * A DUI is `03883848-7` on the scan, `038838487` in one firm's spreadsheet, `3883848-7` in another,
 * and `3883848` by the time Excel has read the column as a number and eaten the leading zero. All
 * four are the same person. Matching them as raw strings finds none of it.
 *
 * ## This is a comparison key, never a stored value
 *
 * Nothing here rewrites what is saved. `entities.id_number` keeps whatever form it arrived in —
 * the number as the document itself prints it — because that is what gets typed into a legal
 * instrument, hyphen and all. These functions only decide whether two spellings denote the same
 * identifier.
 *
 * Worth knowing why the database does not settle this on its own: `entities_identity_key` is unique
 * on the RAW string, so `03883848-7` and `3883848` are two rows as far as Postgres is concerned. It
 * prevents byte-identical duplicates; a sheet fork is never byte-identical. Uniqueness is also per
 * ORGANIZATION, not global.
 *
 * ## Why leniency is bounded
 *
 * `identityCompareKey` folds punctuation and case, which is safe: no jurisdiction distinguishes two
 * identifiers by a hyphen. `identityDigitsKey` additionally strips leading zeros, which is a real
 * risk — it merges `0123` with `123` — and is therefore used only as a SECOND pass, after the strict
 * key finds nothing. Excel eating a leading zero is common enough that refusing to look would reject
 * valid sheets; letting it outrank an exact match would be worse.
 *
 * And matching is not the last line of defence in either direction: every batch row's status is
 * shown in the review table before anything is created, so a miss is an orange row the user resolves
 * rather than a silent fork.
 */

/**
 * Strict key: case and punctuation folded, everything else kept.
 *
 * `03883848-7` → `038838487`. `n.i.t. 0614-010203-102-4` → `NIT06140102031024`.
 */
export function identityCompareKey(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
}

/**
 * Lenient key: digits only, leading zeros dropped — the shape a number takes after a spreadsheet has
 * read it as a number.
 *
 * Returns '' for anything with no digits, and for a value whose digits are all zero, so an empty
 * cell can never match another empty cell as though they were the same person.
 */
export function identityDigitsKey(raw: string | null | undefined): string {
  if (!raw) return ''
  const digits = raw.replace(/\D/g, '').replace(/^0+/, '')
  return digits
}

/** Do two spellings denote the same identifier? Strict first, then the zero-tolerant fallback. */
export function sameIdentity(a: string | null | undefined, b: string | null | undefined): boolean {
  const strictA = identityCompareKey(a)
  const strictB = identityCompareKey(b)
  if (!strictA || !strictB) return false
  if (strictA === strictB) return true
  const digitsA = identityDigitsKey(a)
  const digitsB = identityDigitsKey(b)
  return !!digitsA && digitsA === digitsB
}

/**
 * Index a set of known identifiers for lookup, keeping the two passes separate.
 *
 * Both maps keep the FIRST value seen for a key. A collision means two stored entities already
 * share an identifier under this folding — a pre-existing duplicate, not something to resolve here —
 * and picking the first at least makes the outcome stable rather than dependent on row order.
 */
export function indexIdentities<T>(items: T[], numberOf: (item: T) => string | null | undefined): {
  strict: Map<string, T>
  digits: Map<string, T>
} {
  const strict = new Map<string, T>()
  const digits = new Map<string, T>()
  for (const item of items) {
    const raw = numberOf(item)
    const s = identityCompareKey(raw)
    if (s && !strict.has(s)) strict.set(s, item)
    const d = identityDigitsKey(raw)
    if (d && !digits.has(d)) digits.set(d, item)
  }
  return { strict, digits }
}

/** Look a raw number up in an index built by `indexIdentities`. Strict wins over lenient. */
export function findIdentity<T>(index: { strict: Map<string, T>, digits: Map<string, T> }, raw: string | null | undefined): T | undefined {
  const s = identityCompareKey(raw)
  if (s) {
    const hit = index.strict.get(s)
    if (hit) return hit
  }
  const d = identityDigitsKey(raw)
  return d ? index.digits.get(d) : undefined
}
